import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { ResourceDraft } from '../models/resource-draft';
import { environment } from '../../environments/environment';
import { ResourceModel } from '../models/resource-model';
import { lastValueFrom } from 'rxjs';
import { ResourceMetaData, ResourceType } from '../models/resource-meta-data';
import { IWizardService } from '../interfaces/wizard-service.interface';
import { BasePluginData } from '../models/wizard-plugin-models/base-plugin-data';
import { ResourceConfig } from '../models/configs/resource-config';
import { Log, IShortLogger } from './logger.service';



@Injectable({
  providedIn: 'root'
})
export class ResourceCreatorService implements IWizardService {
  private readonly logger: IShortLogger<ResourceCreatorService> = Log.create(ResourceCreatorService);

  public serviceUrl = environment.apiUrl;
  constructor(public http: HttpClient) {
  }

  public create(finalData: { [key: string]: BasePluginData }, config: ResourceConfig | undefined): Observable<any> {
    const draft = new ResourceDraft();
    draft.data = finalData;
    if (config) {
      draft.metaData = ResourceMetaData.fromConfig(config);
    }
    // TODO: INTERCEPTOR-LOGGING - Remove HTTP request data logging, handle via request-response interceptor
    this.logger.i('Draft to send to API:', draft);
    const headers = { 'content-type': 'application/json' };
    const body = JSON.stringify(draft);
    return this.http.post(
      `${this.serviceUrl}/v1/resources/new`, body, { headers },
    );
  }

  public getAll(): Observable<any> {
    return this.http.get(`${this.serviceUrl}/v1/resources`);
  }

  public getResource(id: string): Observable<any> {
    return this.http.get(`${this.serviceUrl}/v1/resources/${id}`);
  }

  /** Returns display-only metadata (name, CloudProviderData, SoftwareData) without operational fields. */
  public getResourceMetadata(id: string): Observable<any> {
    return this.http.get(`${this.serviceUrl}/v1/resources/${id}/metadata`);
  }

  public async getResourcesByType(type: ResourceType): Promise<ResourceModel[]> {
    const resources$ = this.getAll();
    const resources: ResourceModel[] = await lastValueFrom(resources$);
    const filteredResources = resources.filter(resource => resource.metaData.type === type);
    // TODO: INTERCEPTOR-LOGGING - Remove data filtering debug logging, can be handled via interceptor if needed
    this.logger.i(`Filtered resources of type ${type}:`, filteredResources);
    return filteredResources;
  }
  public finalize(id: string): Observable<any> {
    return this.http.patch(`${this.serviceUrl}/v1/resources/${id}`, null);
  }

  /**
   * Assigns (or clears) the VM Connection Template attached to an org-level template
   * resource. Pass `null` to clear the assignment and revert to the global
   * appsettings.json defaults at next launch. Mirrors
   * `ClassEntityService.assignVmConnectionTemplate` for the per-class equivalent —
   * the two scopes are intentionally independent.
   */
  public assignVmConnectionTemplate(resourceId: string, templateId: string | null): Observable<any> {
    return this.http.patch(
      `${this.serviceUrl}/v1/resources/${encodeURIComponent(resourceId)}/vm-connection-template`,
      { vmConnectionTemplateId: templateId },
    );
  }

  /**
   * Archives a resource template (soft-delete). Blocked by the server if any linked
   * class is still active (returns 409 in that case).
   */
  archiveTemplate(id: string): Observable<void> {
    return this.http.patch<void>(
      `${this.serviceUrl}/v1/resources/${encodeURIComponent(id)}/archive`,
      null
    );
  }

}
